/**
 * #2098 — the person search and the direct conversation, in the shapes the
 * panel renders.
 *
 * Pure, and separate from `chatService.js` for the reason `chatShapes.js` is:
 * the service module pulls in axios, `./api` (Vue + Vuex + the store) and the
 * Firebase SDK and does not load under node, and the mistakes in this area are
 * all silent — a search that fires on the first keystroke and enumerates a
 * local, or a conversation labelled with the wrong person's name.
 *
 * Against cs3-chat `1206a0ec`, which is what runs on the stage chat box:
 *
 *   GET  /api/me/people?q=   → { data: [{ uid, name, role, client_id }] }
 *   POST /api/me/direct {uid} → { data: { id, kind: 'direct', key, name, … , created } }
 */

import { colourFor, initialsFor, normaliseChannel } from './chatShapes.js';

/**
 * ⚠ TWO CHARACTERS, AND IT MUST MATCH `DirectReach::MIN_QUERY`.
 *
 * The server returns an empty list below its own floor rather than an error, so
 * a panel that asked on the first keystroke would get nothing back and look
 * broken — and if the floors ever differed, the shorter one would simply be a
 * request per keystroke that can only answer "[]". One character against a
 * local of two thousand is an enumeration, not a search, which is why there is
 * a floor at all.
 */
export const MIN_QUERY = 2;

/** What the server will return at most (`DirectReach::MAX_RESULTS`). */
export const MAX_RESULTS = 50;

/**
 * How long to wait after the last keystroke before asking.
 *
 * ⚠ THE SEARCH IS A SERVER QUERY WITH A `LIKE` ON IT, not a local filter. At
 * 250 ms a name typed at speed is one request instead of eight, and the panel
 * still feels immediate.
 */
export const DEBOUNCE_MS = 250;

/** Is this worth sending? */
export function searchable(query) {
    return String(query === null || query === undefined ? '' : query).trim().length >= MIN_QUERY;
}

/**
 * One search result, with the avatar the panel draws.
 *
 * `colourFor`/`initialsFor` are the same helpers the message list uses, so one
 * person is the same colour and the same initials everywhere in the panel —
 * deriving them separately here is how the search result and the message header
 * end up looking like two people.
 */
export function normalisePerson(raw) {
    const r = raw || {};
    const name = String(r.name == null ? '' : r.name).trim() || String(r.uid || '');

    return {
        uid: String(r.uid == null ? '' : r.uid),
        name,
        role: r.role || 'member',
        clientId: r.client_id === undefined || r.client_id === null ? null : String(r.client_id),
        initials: initialsFor(name),
        colour: colourFor(r.uid || name),
        // Filled in by the panel from `chatPresence`; here so the shape is one
        // object and the template has no `||` in it.
        online: false,
    };
}

/** @param {Array} rows the API's `data` */
export function normalisePeople(rows) {
    if (!Array.isArray(rows)) return [];

    return rows
        .map((r) => normalisePerson(r))
        .filter((p) => p.uid !== '');
}

/**
 * Is this person CallSteward staff?
 *
 * Used for the "CallSteward" tag beside a result: a member searching their own
 * local sees colleagues and support in one list, and support is worth marking
 * because the conversation is with a different organisation.
 */
export function isStaffRole(role) {
    return role === 'cs_admin' || role === 'cs_support';
}

/**
 * The direct channel from `POST /api/me/direct`, in the panel's channel shape.
 *
 * `normaliseChannel` is the same function the roster uses, so a conversation
 * opened from search and the same conversation after a reload are one shape —
 * two shapes here is how the composer works on one and not the other.
 */
export function normaliseDirect(raw) {
    const channel = normaliseChannel(raw);
    channel.created = (raw || {}).created === true;

    return channel;
}

/**
 * The OTHER person in a direct channel.
 *
 * The id and the `key` are `uidA.uidB` with the uids SORTED (cs3-chat
 * `Channel::pairKey`), so the counterpart is whichever half is not mine. Null
 * when this is not a direct channel or the key does not name me.
 */
export function otherUid(channel, meUid) {
    const c = channel || {};
    if (c.kind !== 'direct' || typeof c.key !== 'string') return null;

    const pair = c.key.split('.');
    if (pair.length !== 2) return null;
    if (pair[0] === meUid) return pair[1];
    if (pair[1] === meUid) return pair[0];

    return null;
}

/**
 * What to call a direct channel in the panel.
 *
 * ⚠ THE STORED NAME IS SYMMETRIC — "Ada Lovelace & Alan Turing" — because one
 * string has to serve both people (cs3-chat `DirectMessages::name`). The panel
 * must show the OTHER person, so the name is split on " & " and the half that
 * is mine is dropped.
 *
 * ⚠ AND THE FALLBACK IS THE WHOLE STORED NAME, NEVER A GUESS. A display name
 * containing " & " ("Smith & Sons", a venue contact) splits into three parts
 * and no half can be trusted, so the symmetric name is shown rather than half
 * of someone's name. Same when the viewer's own name is not one of the halves,
 * which happens when a name was changed after the conversation was created.
 *
 * The real fix is for cs3-chat to send the counterpart's uid and name on a
 * direct channel row — the roster has neither today, and that is raised on the
 * MR rather than worked around with an extra request per conversation.
 *
 * @param {object} channel  a normalised channel
 * @param {string} meName   the viewer's own display name, from the session
 * @param {object} known    optional uid → name, e.g. people already searched
 */
export function directLabel(channel, meName, known) {
    const c = channel || {};
    const stored = String(c.name == null ? '' : c.name).trim();

    // Best source: a name we already have for the counterpart.
    const other = typeof c.otherUid === 'string' ? c.otherUid : null;
    if (other && known && typeof known === 'object' && known[other]) {
        return String(known[other]);
    }

    const parts = stored.split(' & ');
    if (parts.length !== 2) return stored;

    const mine = String(meName == null ? '' : meName).trim();
    if (mine === '') return stored;
    if (parts[0] === mine) return parts[1];
    if (parts[1] === mine) return parts[0];

    return stored;
}

/**
 * Give a direct channel its panel identity: who it is with, and what to call it.
 *
 * Applied to every direct channel in the roster as well as to one just opened,
 * so a reload cannot show a different label from the one the user clicked.
 */
export function labelDirect(channel, meUid, meName, known) {
    const c = channel || {};
    if (c.kind !== 'direct') return c;

    c.otherUid = otherUid(c, meUid);
    c.label = directLabel(c, meName, known);
    // The channel-list row prints "#name"; a conversation is a person, not a
    // channel, so the hash is dropped by the template on `grp === 'direct'`.
    c.grp = 'direct';

    return c;
}

/**
 * `GET /api/me/people` failures, as codes the panel has a sentence for.
 *
 * 403 cannot happen on a `me` endpoint — there is no id in the path to be
 * refused — but it is mapped rather than left to the generic line, because the
 * one thing worse than an unexpected error is an unexpected error described as
 * something else.
 */
export function peopleFailureCode(status) {
    return {
        401: 'chat_session_expired',
        403: 'chat_not_permitted',
        422: 'chat_search_rejected',
    }[status] || 'chat_unreachable';
}

/**
 * `POST /api/me/direct` failures.
 *
 * ⚠ 403 IS THE INTERESTING ONE and it is not an error in the usual sense: it
 * is cs3-chat saying this person may not be messaged under Ed's rule — they
 * have left the client, or the group that made them findable has been switched
 * off since the search returned. The panel says so in those words rather than
 * "chat is unavailable".
 *
 * 404 is "no such person", which cs3-chat answers before the rule check. 422 is
 * the pair that cannot have a channel — two CS staff, or yourself.
 */
export function directFailureCode(status) {
    return {
        401: 'chat_session_expired',
        403: 'direct_not_allowed',
        404: 'direct_no_such_person',
        422: 'direct_not_possible',
    }[status] || 'chat_unreachable';
}
